import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux"; // ✅ Added
import Loader from "../../../loader/Loader";
import { domain } from "../../../../security";
import profile from "../../../../Images/profile.jpg";
import SearchableDropdown from "../../../../UI/common/SearchableDropdown";
import { selectUserID } from "../../../../redux/IchthusSlice"; // ✅ Added

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const AddEmployee = ({ onClose, refreshData, employeeToEdit }) => {
  // --- REDUX STATE ---
  const currentUserID = useSelector(selectUserID); // ✅ Internal Redux usage

  // --- STATE MANAGEMENT ---
  const [activeTab, setActiveTab] = useState("basic");
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(profile);
  const [locations, setLocations] = useState([]);

  const [formData, setFormData] = useState({
    employeeImage: null,
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    sex: "Male",
    civilStatus: "Single",
    nationality: "",
    mobileNumber: "",
    currentAddress: "",
    permanentAddress: "",
    employmentType: "Full-Time",
    position: "",
    department: "",
    dateHired: new Date().toISOString().split("T")[0],
    dateRegularized: "",
    employmentStatus: "Active",
    workSchedule: "",
    locationId: "",
  });

  // --- EFFECTS ---
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toISOString().split("T")[0];
    } catch (error) {
      return "";
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${domain}/api/Locations`);
        setLocations(response.data);
      } catch (error) {
        toast.error("Failed to fetch locations.");
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    if (employeeToEdit) {
      setFormData({
        ...employeeToEdit,
        employeeImage: null,
        sex: employeeToEdit.sex || "Male",
        civilStatus: employeeToEdit.civilStatus || "Single",
        employmentType: employeeToEdit.employmentType || "Full-Time",
        employmentStatus: employeeToEdit.employmentStatus || "Active",
        dateHired: formatDateForInput(employeeToEdit.dateHired),
        dateRegularized: formatDateForInput(employeeToEdit.dateRegularized),
        dateOfBirth: formatDateForInput(employeeToEdit.dateOfBirth),
        locationId: employeeToEdit.locationId || "",
      });

      if (employeeToEdit.employeeImage) {
        setPreviewImage(
          `data:image/jpeg;base64,${employeeToEdit.employeeImage}`,
        );
      } else {
        setPreviewImage(profile);
      }
    }
  }, [employeeToEdit]);

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith("blob:")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  // --- HANDLERS ---
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleLocationChange = (selectedLocationId) => {
    setFormData({ ...formData, locationId: selectedLocationId });
  };

  const handleGenderChange = (gender) => {
    setFormData({ ...formData, sex: gender });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imagePreviewUrl = URL.createObjectURL(file);
      setPreviewImage(imagePreviewUrl);
      setFormData({ ...formData, employeeImage: file });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const apiUrl = `${domain}/api/Employees`;

    let imageBase64 = null;
    if (formData.employeeImage) {
      const base64String = await toBase64(formData.employeeImage);
      imageBase64 = base64String ? base64String.split(",")[1] : null;
    }

    const payload = {
      ...formData,
      employeeImage: imageBase64,
      UserID: currentUserID, // ✅ Sent as raw int
      dateRegularized: formData.dateRegularized || null,
      dateOfBirth: formData.dateOfBirth || null,
      locationId: formData.locationId
        ? parseInt(formData.locationId, 10)
        : null,
    };

    try {
      if (employeeToEdit) {
        if (!payload.employeeImage) {
          delete payload.employeeImage;
        }
        await axios.put(`${apiUrl}/${employeeToEdit.id}`, payload);
        toast.success("Employee updated successfully!");
      } else {
        await axios.post(apiUrl, payload);
        toast.success("Employee added successfully!");
      }
      refreshData();
      onClose();
    } catch (error) {
      toast.error(
        `Error: ${error.response?.data?.title || "Failed to save employee"}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // --- RENDER TABS ---
  const renderBasicInfoTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="flex-shrink-0 flex flex-col items-center">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Employee Image
          </label>
          <div className="w-32 h-32 overflow-hidden rounded-full border-2 border-gray-300 mb-3 shadow-inner">
            <img
              src={previewImage}
              alt="Preview"
              className="object-cover w-full h-full"
              onError={(e) => (e.target.src = profile)}
            />
          </div>
          <input
            type="file"
            id="employeeImage"
            onChange={handleImageChange}
            accept="image/*"
            className="text-xs text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
          />
        </div>

        <div className="w-full grid grid-cols-1 gap-4">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700"
            >
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 outline-none"
              required
            />
          </div>
          <div>
            <label
              htmlFor="middleName"
              className="block text-sm font-medium text-gray-700"
            >
              Middle Name
            </label>
            <input
              type="text"
              id="middleName"
              value={formData.middleName}
              onChange={handleInputChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 outline-none"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700"
            >
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 outline-none"
              required
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label
            htmlFor="dateOfBirth"
            className="block text-sm font-medium text-gray-700"
          >
            Date of Birth
          </label>
          <input
            type="date"
            id="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="mobileNumber"
            className="block text-sm font-medium text-gray-700"
          >
            Phone Number
          </label>
          <input
            type="tel"
            id="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleInputChange}
            placeholder="09XXXXXXXXX"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            required
          />
        </div>
        <div>
          <label
            htmlFor="civilStatus"
            className="block text-sm font-medium text-gray-700"
          >
            Civil Status
          </label>
          <select
            id="civilStatus"
            value={formData.civilStatus}
            onChange={handleInputChange}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          >
            <option>Single</option>
            <option>Married</option>
            <option>Divorced</option>
            <option>Widowed</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="nationality"
            className="block text-sm font-medium text-gray-700"
          >
            Nationality
          </label>
          <input
            type="text"
            id="nationality"
            value={formData.nationality}
            onChange={handleInputChange}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Gender
        </label>
        <div className="flex gap-2 mt-2">
          {["Male", "Female", "Other"].map((gender) => (
            <button
              key={gender}
              type="button"
              onClick={() => handleGenderChange(gender)}
              className={`px-4 py-2 text-sm font-medium rounded-md border transition-all ${
                formData.sex === gender
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {gender}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="currentAddress"
            className="block text-sm font-medium text-gray-700"
          >
            Current Address
          </label>
          <textarea
            id="currentAddress"
            rows="2"
            value={formData.currentAddress}
            onChange={handleInputChange}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            required
          />
        </div>
        <div>
          <label
            htmlFor="permanentAddress"
            className="block text-sm font-medium text-gray-700"
          >
            Permanent Address
          </label>
          <textarea
            id="permanentAddress"
            rows="2"
            value={formData.permanentAddress}
            onChange={handleInputChange}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          />
        </div>
      </div>
    </div>
  );

  const renderEmploymentTab = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label
          htmlFor="position"
          className="block text-sm font-medium text-gray-700"
        >
          Job Title
        </label>
        <input
          type="text"
          id="position"
          value={formData.position}
          onChange={handleInputChange}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          required
        />
      </div>
      <div>
        <label
          htmlFor="department"
          className="block text-sm font-medium text-gray-700"
        >
          Department
        </label>
        <input
          type="text"
          id="department"
          value={formData.department}
          onChange={handleInputChange}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          required
        />
      </div>
      <div>
        <label
          htmlFor="employmentType"
          className="block text-sm font-medium text-gray-700"
        >
          Employment Type
        </label>
        <select
          id="employmentType"
          value={formData.employmentType}
          onChange={handleInputChange}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
        >
          <option>Full-Time</option>
          <option>Part-Time</option>
          <option>Contract</option>
          <option>Intern</option>
        </select>
      </div>
      <div>
        <label
          htmlFor="employmentStatus"
          className="block text-sm font-medium text-gray-700"
        >
          Status
        </label>
        <select
          id="employmentStatus"
          value={formData.employmentStatus}
          onChange={handleInputChange}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
        >
          <option>Active</option>
          <option>Inactive</option>
          <option>On Leave</option>
          <option>Terminated</option>
        </select>
      </div>
      <div>
        <label
          htmlFor="dateHired"
          className="block text-sm font-medium text-gray-700"
        >
          Date Hired
        </label>
        <input
          type="date"
          id="dateHired"
          value={formData.dateHired}
          onChange={handleInputChange}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          required
        />
      </div>
      <div>
        <label
          htmlFor="dateRegularized"
          className="block text-sm font-medium text-gray-700"
        >
          Date Regularized
        </label>
        <input
          type="date"
          id="dateRegularized"
          value={formData.dateRegularized}
          onChange={handleInputChange}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
        />
      </div>
      <div className="sm:col-span-2">
        <label
          htmlFor="workSchedule"
          className="block text-sm font-medium text-gray-700"
        >
          Work Schedule
        </label>
        <input
          type="text"
          id="workSchedule"
          value={formData.workSchedule}
          onChange={handleInputChange}
          placeholder="e.g. Mon-Fri 8AM-5PM"
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          required
        />
      </div>
      <div className="sm:col-span-2">
        <label
          htmlFor="locationId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Location / Branch
        </label>
        <SearchableDropdown
          options={locations}
          value={formData.locationId}
          onChange={handleLocationChange}
          placeholder="Select a location..."
          labelKey="locationName"
          valueKey="id"
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl flex flex-col max-h-[90vh] shadow-2xl">
      {isLoading && <Loader />}

      <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10 rounded-t-2xl">
        <h2 className="text-xl font-bold text-gray-800">
          {employeeToEdit ? "Update Employee" : "Register New Employee"}
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="px-6 bg-white border-b border-gray-50 sticky top-[73px] z-10">
        <nav className="flex space-x-8">
          {["basic", "employment"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-semibold text-sm transition-all ${
                activeTab === tab
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {tab === "basic" ? "Basic Information" : "Employment Details"}
            </button>
          ))}
        </nav>
      </div>

      <form onSubmit={handleFormSubmit} className="flex-grow overflow-y-auto">
        <div className="p-6">
          {activeTab === "basic" ? renderBasicInfoTab() : renderEmploymentTab()}
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 rounded-b-2xl sticky bottom-0">
          {activeTab === "basic" ? (
            <button
              type="button"
              onClick={() => setActiveTab("employment")}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition shadow-md shadow-indigo-100"
            >
              Next Step
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setActiveTab("basic")}
                className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition shadow-md shadow-indigo-100 disabled:opacity-70"
              >
                {isLoading
                  ? "Processing..."
                  : employeeToEdit
                    ? "Update Record"
                    : "Finish & Save"}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddEmployee;
